import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { dashboardAPI } from "../services/api";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalComparisons: 0,
    successRate: "0%",
    totalMasterRecords: 0,
  });

  const [chartData, setChartData] = useState({
    recentComparisons: [],
    comparisonStatus: [],
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all dashboard data concurrently
      const [
        statsResponse,
        recentComparisonsResponse,
        comparisonStatusResponse,
        activitiesResponse,
      ] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRecentComparisons(7),
        dashboardAPI.getComparisonStatus(),
        dashboardAPI.getRecentActivities(10),
      ]);

      // Extract data from axios responses
      const statsData = statsResponse.data.data;
      const recentComparisonsData = recentComparisonsResponse.data.data;
      const comparisonStatusData = comparisonStatusResponse.data.data;
      const activitiesData = activitiesResponse.data.data;

      setStats({
        totalJobs: statsData.total_jobs || 0,
        activeJobs: statsData.active_jobs || 0,
        totalComparisons: statsData.total_comparisons || 0,
        successRate: statsData.success_rate || "0%",
        totalMasterRecords: statsData.total_master_records || 0,
      });

      setChartData({
        recentComparisons: recentComparisonsData.map((item) => ({
          date: item.date,
          count: item.comparison_count,
        })),
        comparisonStatus: comparisonStatusData,
      });

      setRecentActivities(activitiesData);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      setError("ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setLoading(false);
    }
  };

  const pieData = chartData.comparisonStatus.map((item) => ({
    name: item.match_status,
    value: item.count,
    color: getStatusColor(item.match_status),
  }));

  function getStatusColor(status) {
    switch (status) {
      case "MATCHED":
        return "#10b981";
      case "PARTIAL":
        return "#f59e0b";
      case "MISMATCH":
        return "#ef4444";
      case "NOT_FOUND":
        return "#6b7280";
      default:
        return "#6366f1";
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>กำลังโหลดข้อมูล...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <h2>เกิดข้อผิดพลาด</h2>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="retry-button">
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>ภาพรวมของระบบ GELATO</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4">
        <div className="card stat-card">
          <div
            className="stat-icon"
            style={{ background: "#6366f120", color: "#6366f1" }}
          >
            💼
          </div>
          <div className="stat-value">{stats.totalJobs}</div>
          <div className="stat-label">งานทั้งหมด</div>
        </div>

        <div className="card stat-card">
          <div
            className="stat-icon"
            style={{ background: "#ec489920", color: "#ec4899" }}
          >
            ⚡
          </div>
          <div className="stat-value">{stats.activeJobs}</div>
          <div className="stat-label">งานที่กำลังดำเนินการ</div>
        </div>

        <div className="card stat-card">
          <div
            className="stat-icon"
            style={{ background: "#10b98120", color: "#10b981" }}
          >
            ✅
          </div>
          <div className="stat-value">{stats.successRate}</div>
          <div className="stat-label">อัตราความสำเร็จ</div>
        </div>

        <div className="card stat-card">
          <div
            className="stat-icon"
            style={{ background: "#3b82f620", color: "#3b82f6" }}
          >
            📊
          </div>
          <div className="stat-value">
            {stats.totalMasterRecords.toLocaleString()}
          </div>
          <div className="stat-label">ข้อมูล Master Records</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2" style={{ marginTop: "32px" }}>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">การเปรียบเทียบรายวัน</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.recentComparisons}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#6366f1"
                strokeWidth={3}
                dot={{ fill: "#6366f1", r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">สถานะการเปรียบเทียบ</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card" style={{ marginTop: "32px" }}>
        <div className="card-header">
          <h3 className="card-title">กิจกรรมล่าสุด</h3>
        </div>
        <div className="activity-list">
          {recentActivities.length > 0 ? (
            recentActivities.map((activity, index) => (
              <div key={index} className="activity-item">
                <div
                  className="activity-icon"
                  style={{
                    background: getActivityIconColor(activity.type).background,
                    color: getActivityIconColor(activity.type).color,
                  }}
                >
                  {getActivityIcon(activity.type)}
                </div>
                <div className="activity-content">
                  <div className="activity-title">{activity.description}</div>
                  <div className="activity-time">
                    {formatTimeAgo(activity.created_at)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-activities">ไม่มีกิจกรรมล่าสุด</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper functions for activity display
function getActivityIcon(type) {
  switch (type) {
    case "COMPARISON_SUCCESS":
      return "✅";
    case "FILE_UPLOAD":
      return "📁";
    case "JOB_CREATED":
      return "💼";
    case "JOB_COMPLETED":
      return "🎉";
    default:
      return "📋";
  }
}

function getActivityIconColor(type) {
  switch (type) {
    case "COMPARISON_SUCCESS":
    case "JOB_COMPLETED":
      return { background: "#10b98120", color: "#10b981" };
    case "FILE_UPLOAD":
      return { background: "#6366f120", color: "#6366f1" };
    case "JOB_CREATED":
      return { background: "#ec489920", color: "#ec4899" };
    default:
      return { background: "#6b728020", color: "#6b7280" };
  }
}

function formatTimeAgo(dateString) {
  const now = new Date();
  const past = new Date(dateString);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "เมื่อสักครู่";
  if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`;
  if (diffHours < 24) return `${diffHours} ชั่วโมงที่แล้ว`;
  return `${diffDays} วันที่แล้ว`;
}

export default Dashboard;
